import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.jsx'
import './styles/index.css'

// Ultra-Aggressive Cache Busting and Service Worker Management
const APP_VERSION = __APP_VERSION__ || Date.now().toString() // Build timestamp as version
const CACHE_BUSTER_KEY = 'trp_app_version'
const FORCE_RELOAD_KEY = 'trp_force_reload'

// Ultra-aggressive cache clearing
const clearAllCaches = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys()
      console.log('Clearing all caches:', cacheNames)
      await Promise.all(cacheNames.map(name => caches.delete(name)))
      
      // Also clear browser storage
      if ('localStorage' in window) {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.startsWith('trp_') || key.includes('cache') || key.includes('version'))) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
      }
      
      // Clear session storage except certification tab state (certpro_*)
      if ('sessionStorage' in window) {
        const sessionKeysToRemove = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key && !key.startsWith('certpro_')) {
            sessionKeysToRemove.push(key)
          }
        }
        sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key))
      }
      
      console.log('All caches and storage cleared')
    } catch (error) {
      console.warn('Cache clearing failed:', error)
    }
  }
}

// Force service worker update
const forceServiceWorkerUpdate = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    
    for (let registration of registrations) {
      // Force update immediately
      await registration.update()
      
      // If there's a waiting service worker, activate it immediately
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
    }
  }
}

// Enhanced version checking with forced reload capability
const checkVersionAndRefresh = async () => {
  const storedVersion = localStorage.getItem(CACHE_BUSTER_KEY)
  const forceReload = localStorage.getItem(FORCE_RELOAD_KEY)
  const currentVersion = APP_VERSION
  
  // Check if we need to force reload due to service worker update
  if (forceReload === 'true') {
    console.log('Force reload requested by service worker')
    localStorage.removeItem(FORCE_RELOAD_KEY)
    await clearAllCaches()
    window.location.reload(true)
    return
  }
  
  if (storedVersion && storedVersion !== currentVersion) {
    console.log('New version detected, clearing all caches and refreshing...')
    
    // Clear everything
    await clearAllCaches()
    
    // Store new version
    localStorage.setItem(CACHE_BUSTER_KEY, currentVersion)
    
    // Force a hard refresh
    window.location.reload(true)
  } else if (!storedVersion) {
    // First visit, store the version and clear any existing caches
    localStorage.setItem(CACHE_BUSTER_KEY, currentVersion)
    await clearAllCaches()
  }
}

// Enhanced Service Worker Registration with auto-update
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      // Clear all caches first
      await clearAllCaches()
      
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js')
      
      // Check for updates every time the page loads
      await registration.update()
      
      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker is ready, activate it immediately
            newWorker.postMessage({ type: 'SKIP_WAITING' })
          }
        })
      })
      
      // Listen for service worker taking control
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Service worker has been updated, refresh the page
        window.location.reload()
      })
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data) {
          switch (event.data.type) {
            case 'CACHE_UPDATED':
              console.log('Cache updated by service worker, refreshing...')
              window.location.reload(true)
              break
              
            case 'FORCE_RELOAD':
              console.log('Force reload requested by service worker')
              localStorage.setItem(FORCE_RELOAD_KEY, 'true')
              clearAllCaches().then(() => {
                window.location.reload(true)
              })
              break
              
            default:
              console.log('Service worker message:', event.data)
          }
        }
      })
      
      console.log('SW registered and cache management initialized')
      
    } catch (error) {
      console.log('SW registration failed: ', error)
    }
    
    // Check version after service worker setup
    checkVersionAndRefresh()
  })
  
  // Also check version on visibility change (when user returns to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      forceServiceWorkerUpdate()
      setTimeout(checkVersionAndRefresh, 1000)
    }
  })
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
) 